
import { Injectable } from '@angular/core';
import { EMPTY, filter, map, merge, Observable, of, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ExDashboardEventService {
  /**
   * Live stream of dashboard events (sheet load, filter, component reload, etc.).
   * A plain Subject does not replay; see {@link lastPayloadByEventName} for replay.
   */
  private eventSubject = new Subject<{ name: string; data: any }>();

  /**
   * Last payload per event **name** so new subscribers do not miss events that
   * fired before they subscribed. A single BehaviorSubject only kept the
   * globally last event, so e.g. `sheet-B:loaded` overwrote `sheet-A:loaded`
   * and resolvers for sheet A never received a replay (charts stuck loading).
   */
  private lastPayloadByEventName = new Map<string, any>();

  constructor() { }

  on(eventName: string): Observable<any> {
    const replay$ = this.lastPayloadByEventName.has(eventName)
      ? of(this.lastPayloadByEventName.get(eventName))
      : EMPTY;
    return merge(
      replay$,
      this.eventSubject.pipe(
        filter((event: { name: string; data: any }) => event && event.name === eventName),
        map((event: { name: string; data: any }) => event.data)
      )
    );
  }

  broadcast(eventName: string, eventData?: any): void {
    // When a sheet starts loading again, drop any cached `:loaded` / `:error`
    // so a new subscriber mid-reload does not replay stale "loaded" before
    // the new HTTP finishes.
    if (eventName.startsWith('sheet-') && eventName.endsWith(':loading')) {
      const prefix = eventName.slice(0, -':loading'.length);
      this.lastPayloadByEventName.delete(prefix + ':loaded');
      this.lastPayloadByEventName.delete(prefix + ':error');
    }
    this.lastPayloadByEventName.set(eventName, eventData);
    this.eventSubject.next({ name: eventName, data: eventData });
  }
}
